// Resolution Engine client for the dashboard (Mission Control + Case view).
//
// Unlike the sidebar (which proxies through Pages Functions so the org API
// key stays server-side), the dashboard authenticates the *person*: a
// mediator/clerk/admin JWT from POST /auth/login, sent directly to the
// Engine (open CORS). VITE_RESOLUTION_ENGINE_BASE_URL already includes
// /api/v1 — same base the sidebar client uses.

import { env } from '../env';
import type { ApiError, Case, Party } from '../types/case';
import type {
  AuditEntry,
  Availability,
  CaseListResponse,
  CreateDocumentResponse,
  CreateSchedulingTaskResponse,
  DocType,
  DocumentRecord,
  LoginResponse,
  PartyInvoice,
  Principal,
  SchedulingTask,
  SendActionResponse,
  SigningStatus,
  Thread,
  TimelineResponse,
  UnmatchedItem,
} from '../types/dashboard';

export class EngineError extends Error {
  readonly status: number;
  readonly code: string;
  /** Structured error payload (e.g. { conflict, overridable } on
      scheduling blocks a mediator may consciously push through). */
  readonly details?: Record<string, unknown>;
  constructor(status: number, code: string, message: string, details?: Record<string, unknown>) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** A scheduling block the mediator may override (calendar busy / outside
    availability) — retry the same call with overrideConflicts=true. */
export const isOverridableConflict = (err: unknown): err is EngineError =>
  err instanceof EngineError && err.details?.overridable === true;

export const isForbidden = (err: unknown): boolean =>
  err instanceof EngineError && err.status === 403;

async function request<T>(
  path: string,
  token: string | null,
  init: RequestInit = {},
): Promise<T> {
  const res = await fetch(`${env.resolutionEngineBaseUrl}${path}`, {
    ...init,
    headers: {
      ...(init.headers ?? {}),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(init.body ? { 'content-type': 'application/json' } : {}),
    },
  });

  // 207 = multi-status (per-recipient send results) — still a JSON body.
  if (!res.ok && res.status !== 207) {
    const body = (await res.json().catch(() => null)) as ApiError | null;
    throw new EngineError(
      res.status,
      body?.error?.code ?? 'UNKNOWN',
      body?.error?.message ?? `Resolution Engine error (${res.status})`,
      (body?.error as { details?: Record<string, unknown> } | undefined)?.details,
    );
  }
  return (await res.json()) as T;
}

// ============================================================
// Auth
// ============================================================

export function login(email: string, password: string): Promise<LoginResponse> {
  return request<LoginResponse>('/auth/login', null, {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export function me(token: string): Promise<Principal & { org_scope: string[] }> {
  return request('/auth/me', token, { method: 'GET' });
}

// ============================================================
// Cases
// ============================================================

export function listCases(
  token: string,
  query: { status?: string; priority?: string; sort?: string; order?: 'asc' | 'desc'; limit?: number; cursor?: string; ids?: string[]; org_id?: string; tag?: string; assigned?: 'me' | 'none'; include_archived?: boolean; unscheduled?: boolean; followed?: boolean } = {},
): Promise<CaseListResponse> {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (Array.isArray(v)) {
      if (v.length) params.set(k, v.join(','));
    } else if (v !== undefined && v !== '') params.set(k, String(v));
  }
  const qs = params.toString();
  return request<CaseListResponse>(`/cases${qs ? `?${qs}` : ''}`, token, { method: 'GET' });
}

/** Fetch EVERY page of the case list (cursor loop), not just the first 100 —
    a mediator can legitimately own more cases than one page (Scott took all
    134 Nomad cases, 2026-10-26). Pages sequentially; hard ceiling keeps a
    runaway cursor from looping forever. */
export async function listAllCases(
  token: string,
  query: Parameters<typeof listCases>[1] = {},
  maxPages = 10,
): Promise<CaseListResponse> {
  const all: CaseListResponse['data'] = [];
  const seen = new Set<string>();
  let cursor: string | undefined;
  let last: CaseListResponse | null = null;
  for (let page = 0; page < maxPages; page++) {
    last = await listCases(token, { ...query, limit: 100, ...(cursor ? { cursor } : {}) });
    for (const c of last.data) if (!seen.has(c.id)) { seen.add(c.id); all.push(c); }
    if (!last.pagination.cursor || last.data.length === 0) break;
    cursor = last.pagination.cursor;
  }
  return { data: all, pagination: { total: last?.pagination.total ?? all.length, limit: all.length, cursor: null } };
}

/** Follow / unfollow a case — a per-mediator bookmark (Admin Mediator Phase 2). */
export function followCase(token: string, caseId: string, follow: boolean): Promise<{ ok: boolean; followed: boolean }> {
  return request(`/cases/${encodeURIComponent(caseId)}/follow`, token, { method: follow ? 'POST' : 'DELETE' });
}

/** Staff-created case — no automated messaging, optional pre-agreed
    mediation session (Danny, 2026-08-24). */
export function createManualCase(
  token: string,
  body: {
    org_id: string;
    case_number?: string;
    trial_date?: string;
    mediation_start?: string;
    description?: string;
    parties: Array<{ name: string; side: 'claimant' | 'respondent'; email?: string; phone?: string }>;
    send_reminders?: boolean;
  },
): Promise<{ case_id: string; status: string; session_id: string | null; join_url: string | null }> {
  return request('/cases/manual', token, { method: 'POST', body: JSON.stringify(body) });
}

/** Assignable mediators for a source (staff dropdowns). */
export function getMediatorRoster(
  token: string,
  orgId: string,
): Promise<{ mediators: Array<{ id: string; display_name: string; role: string }> }> {
  return request(`/mediators/roster?org_id=${encodeURIComponent(orgId)}`, token);
}

/** Assign/reassign the case's mediator (null unassigns). */
export function assignCaseMediator(
  token: string,
  caseId: string,
  mediatorId: string | null,
): Promise<{ ok: boolean; assigned_mediator_id: string | null; mediator_name: string | null; session_reassigned?: 'rebuilt' | 'none' | 'failed'; session_reassign_error?: string | null }> {
  return request(`/cases/${encodeURIComponent(caseId)}/assign-mediator`, token, {
    method: 'POST',
    body: JSON.stringify({ mediator_id: mediatorId }),
  });
}

/** Correct the case's court date (null clears). Audited old -> new. */
export function setTrialDate(
  token: string,
  caseId: string,
  trialDate: string | null,
): Promise<{ ok: boolean; trial_date: string | null }> {
  return request(`/cases/${encodeURIComponent(caseId)}/trial-date`, token, {
    method: 'POST',
    body: JSON.stringify({ trial_date: trialDate }),
  });
}

/** Set/clear the case's display caption (overrides party-derived). */
export function setCaseName(
  token: string,
  caseId: string,
  caseName: string | null,
): Promise<{ ok: boolean; case_name: string | null }> {
  return request(`/cases/${encodeURIComponent(caseId)}/case-name`, token, {
    method: 'POST',
    body: JSON.stringify({ case_name: caseName }),
  });
}

/** Lift a court case's service-of-process hold: confirms the Defendant was
    served, which releases the engine to begin Defendant outreach. Throws a
    409 NOT_ON_HOLD EngineError when the case wasn't on hold. Audited. */
export function confirmService(
  token: string,
  caseId: string,
): Promise<{ ok: boolean; confirmed: boolean; defendantOutreachStarted: boolean; needsDefendantInfo: boolean }> {
  return request(`/cases/${encodeURIComponent(caseId)}/confirm-service`, token, {
    method: 'POST',
  });
}

export function getCaseDetail(token: string, caseId: string): Promise<Case> {
  return request<Case>(`/cases/${encodeURIComponent(caseId)}`, token, { method: 'GET' });
}

/** Queue search: party names, message content (withheld from clerks
 *  server-side), and case numbers. */
export interface CaseSearchHit {
  case_id: string;
  matched: string[]; // 'party' | 'message' | 'case_number'
  snippet: string | null;
}

export function searchCases(token: string, q: string): Promise<{ results: CaseSearchHit[] }> {
  return request<{ results: CaseSearchHit[] }>(`/cases/search?q=${encodeURIComponent(q)}`, token);
}

/** Edit a party's contact card. Empty string clears email/phone. */
export interface AdminPractice {
  id: string; name: string; status: string;
  owner: { email?: string; display_name?: string } | null;
  onboarding: Record<string, unknown>;
  created_at: string;
  sources: Array<{ id: string; name: string; slug: string | null }>;
}

export function adminListPractices(token: string): Promise<{ practices: AdminPractice[] }> {
  return request('/admin/practices', token, { method: 'GET' });
}

export function adminCreatePractice(
  token: string,
  body: { name: string; owner_email: string; owner_name: string; intake_slug?: string },
): Promise<{ practice_id: string; invite_link: string; invite_emailed: boolean; intake_url: string | null }> {
  return request('/admin/practices', token, { method: 'POST', body: JSON.stringify(body) });
}

export function getMyPractice(token: string): Promise<{
  practice: {
    id: string; name: string; status: string;
    checklist: { zoom_connected: boolean; availability_set: boolean; branding_set: boolean; intake_link: string | null; first_case_created: boolean };
  } | null;
}> {
  return request('/mediators/me/practice', token, { method: 'GET' });
}

export function getZoomOwnConnection(token: string): Promise<{ configured: boolean; connection: { status: string; account_email: string | null; zoom_user_id: string | null } | null }> {
  return request('/mediators/me/zoom-connection', token, { method: 'GET' });
}

export function startZoomOwnConnect(token: string): Promise<{ url: string }> {
  return request('/mediators/me/zoom-connection/start', token, { method: 'POST', body: '{}' });
}

export function disconnectZoomOwn(token: string): Promise<{ ok: boolean }> {
  return request('/mediators/me/zoom-connection', token, { method: 'DELETE' });
}

export function ensureSessionMeeting(
  token: string,
  caseId: string,
  sessionId: string,
): Promise<{ ok: boolean; join_url?: string | null; already?: boolean }> {
  return request(`/cases/${encodeURIComponent(caseId)}/sessions/${encodeURIComponent(sessionId)}/ensure-meeting`, token, {
    method: 'POST',
    body: '{}',
  });
}

export function deleteCaseParty(token: string, caseId: string, partyId: string): Promise<{ deleted: boolean }> {
  return request(`/cases/${encodeURIComponent(caseId)}/parties/${encodeURIComponent(partyId)}`, token, {
    method: 'DELETE',
  });
}

export function updateCaseParty(
  token: string,
  caseId: string,
  partyId: string,
  body: {
    name?: string;
    email?: string;
    phone?: string;
    relationship?: string;
    business_name?: string;
    business_role?: string;
    /** Scheduling v2 §5: does this person get availability asks, and whom
        do they schedule for (proxy). */
    is_selector?: boolean;
    acts_for?: string[];
    /** Per-party invoicing flag: true = invoice this party, false = never,
        unset = default (claimants/respondents). */
    invoice_recipient?: boolean;
    role?: string;
    role_archetype?: 'claimant' | 'respondent';
    communication_preference?: 'sms' | 'email' | null;
    interpreter_needed?: boolean;
    fees_waived?: boolean;
    alt_emails?: string[];
    alt_phones?: string[];
  },
): Promise<Party> {
  return request<Party>(
    `/cases/${encodeURIComponent(caseId)}/parties/${encodeURIComponent(partyId)}`,
    token,
    { method: 'PATCH', body: JSON.stringify(body) },
  );
}

export interface BusinessContactCandidate {
  found: boolean;
  business_name: string;
  email: string | null;
  phone: string | null;
  website: string | null;
  source_url: string | null;
  note: string;
}

/** Good-faith missing-party acquisition (§8): one internet search for a
    BUSINESS party's public contact. Returns a candidate for review; never
    applies it or emails. 422 NOT_A_BUSINESS if the name is an individual. */
export function findPartyContact(
  token: string,
  caseId: string,
  partyId: string,
): Promise<{ candidate: BusinessContactCandidate }> {
  return request<{ candidate: BusinessContactCandidate }>(
    `/cases/${encodeURIComponent(caseId)}/parties/${encodeURIComponent(partyId)}/find-contact`,
    token,
    { method: 'POST' },
  );
}

/** Send the web-found BUSINESS contact a formal "To Whom It May Concern"
    coordination note asking them to route us to the right person, with a
    3-business-day deadline. 409 NO_CONTACT (run Find contact first / add an
    email or phone) · 409 SEND_FAILED (already sent today) · 403 FORBIDDEN. */
export function sendCoordinationNote(
  token: string,
  caseId: string,
  partyId: string,
): Promise<{ ok: boolean; queued: number; due_at: string }> {
  return request(
    `/cases/${encodeURIComponent(caseId)}/parties/${encodeURIComponent(partyId)}/send-coordination-note`,
    token,
    { method: 'POST', body: '{}' },
  );
}

/** Add an additional participant (lawyer, roommate, family…) to a case. */
export function addCaseParty(
  token: string,
  caseId: string,
  body: { name: string; side?: 'claimant' | 'respondent' | 'participant'; relationship?: string; email?: string; phone?: string },
): Promise<Party> {
  return request<Party>(`/cases/${encodeURIComponent(caseId)}/parties`, token, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Authoritative counts for the "Hi {name}" tiles — the viewer's OWN open
    caseload + how many await a reply, computed server-side over the full set
    (not the loaded page, not the source filter). */
export interface CaseSummary {
  open: number;
  awaiting_reply: number;
  awaiting_case_ids: string[];
  /** Admin/admin-mediator only: unassigned open cases with an unanswered inbound — the ownership-gap safety net. */
  unassigned_awaiting?: number;
  unassigned_awaiting_case_ids?: string[];
}

export function getCaseSummary(token: string): Promise<CaseSummary> {
  return request<CaseSummary>(`/cases/summary`, token);
}

/** Mark a case's messages read — drops it from the awaiting-reply ("Unread
    messages") list without replying; a newer inbound re-raises it. */
export function markCaseMessagesRead(token: string, caseId: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/cases/${encodeURIComponent(caseId)}/messages/mark-read`, token, {
    method: 'POST',
    body: '{}',
  });
}

/** Promote the property owner on file into a claimant-side signing party in
    one click (idempotent). Toggle OFF reuses deleteCaseParty. */
export function addOwnerAsParty(token: string, caseId: string): Promise<Party & { already?: boolean }> {
  return request<Party & { already?: boolean }>(`/cases/${encodeURIComponent(caseId)}/parties/from-owner`, token, {
    method: 'POST',
    body: '{}',
  });
}

export interface SchedulingHistoryResponse {
  tasks: Array<{
    id: string; status: string; created_at: string;
    window_start: string | null; window_end: string | null;
    deadline_at: string | null; booked_slot: string | null;
    reask_round: number; no_overlap: boolean; mediator_blocked: boolean;
    participants: Array<{
      party_id: string; name: string;
      submitted_at: string | null; declined_at: string | null; confirmed_slot: string | null;
      selected_slots: string[]; preferred_slots: string[]; availability_text: string | null;
      asks: { count: number; last_at: string | null };
      nudges: { count: number; last_at: string | null };
      call_flagged_at: string | null; responsive: boolean;
    }>;
  }>;
  total_asks: number;
  total_nudges: number;
}

export function listSchedulingTasks(
  token: string,
  caseId: string,
): Promise<{ tasks: Array<{ id: string; status: string; created_at: string; booked_slot: string | null }> }> {
  return request(`/cases/${encodeURIComponent(caseId)}/scheduling-tasks`, token, { method: 'GET' });
}

export interface WaterfallReport {
  orgs: Array<{ id: string; name: string }>;
  org_id: string;
  report: { from: string; to: string; steps: Array<{ key: string; label: string; count: number }> } | null;
}

export function getWaterfallReport(token: string, q: { org_id?: string; from: string; to: string }): Promise<WaterfallReport> {
  const p = new URLSearchParams({ from: q.from, to: q.to, ...(q.org_id ? { org_id: q.org_id } : {}) });
  return request<WaterfallReport>(`/metrics/waterfall?${p}`, token, { method: 'GET' });
}

export function getSchedulingHistory(token: string, caseId: string): Promise<SchedulingHistoryResponse> {
  return request<SchedulingHistoryResponse>(`/cases/${encodeURIComponent(caseId)}/scheduling-history`, token, { method: 'GET' });
}

export function getCaseAudit(
  token: string,
  caseId: string,
  limit = 100,
  cursor?: string | null,
): Promise<{ data: AuditEntry[]; pagination?: { total: number | null; cursor: string | null } }> {
  const qs = `limit=${limit}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`;
  return request<{ data: AuditEntry[]; pagination?: { total: number | null; cursor: string | null } }>(
    `/cases/${encodeURIComponent(caseId)}/audit?${qs}`,
    token,
    { method: 'GET' },
  );
}

// ============================================================
// Comms — timeline, composer, drafts, unmatched inbox
// ============================================================

export function getTimeline(token: string, caseId: string): Promise<TimelineResponse> {
  return request<TimelineResponse>(
    `/cases/${encodeURIComponent(caseId)}/timeline`,
    token,
    { method: 'GET' },
  );
}

export function getThreads(token: string, caseId: string): Promise<{ threads: Thread[] }> {
  return request<{ threads: Thread[] }>(
    `/cases/${encodeURIComponent(caseId)}/threads`,
    token,
    { method: 'GET' },
  );
}

export function sendAction(
  token: string,
  caseId: string,
  input: {
    recipient_party_ids: string[];
    channel: 'sms' | 'email';
    body: string;
    subject?: string;
    attachment_file_ids?: string[];
  },
): Promise<SendActionResponse> {
  return request<SendActionResponse>(
    `/cases/${encodeURIComponent(caseId)}/actions`,
    token,
    { method: 'POST', body: JSON.stringify(input) },
  );
}

export function releaseDraftAction(
  token: string,
  actionId: string,
): Promise<{ ok: boolean; action_id: string; status: string }> {
  return request(`/comms/actions/${encodeURIComponent(actionId)}/release`, token, {
    method: 'POST',
    body: '{}',
  });
}

export function getUnmatched(token: string): Promise<{ unmatched: UnmatchedItem[] }> {
  return request<{ unmatched: UnmatchedItem[] }>('/comms/unmatched', token, { method: 'GET' });
}

export function resolveUnmatched(
  token: string,
  unmatchedId: string,
  input: { thread_id?: string; status?: 'resolved' | 'ignored' },
): Promise<{ ok: boolean }> {
  return request(`/comms/unmatched/${encodeURIComponent(unmatchedId)}/resolve`, token, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// ============================================================
// Scheduling + availability
// ============================================================

export function createSchedulingTask(
  token: string,
  caseId: string,
  input: { session_type?: 'joint' | 'single_party'; category?: 'consultation' | 'mediation'; duration_minutes?: number } = {},
): Promise<CreateSchedulingTaskResponse> {
  return request<CreateSchedulingTaskResponse>(
    `/cases/${encodeURIComponent(caseId)}/scheduling-tasks`,
    token,
    { method: 'POST', body: JSON.stringify(input) },
  );
}

export function getSchedulingTask(token: string, taskId: string): Promise<SchedulingTask> {
  return request<SchedulingTask>(
    `/scheduling-tasks/${encodeURIComponent(taskId)}`,
    token,
    { method: 'GET' },
  );
}

export function proposeSlots(
  token: string,
  taskId: string,
  slots: string[],
  overrideConflicts = false,
): Promise<{ ok: boolean }> {
  return request(`/scheduling-tasks/${encodeURIComponent(taskId)}/propose`, token, {
    method: 'POST',
    body: JSON.stringify({ slots, ...(overrideConflicts ? { override_conflicts: true } : {}) }),
  });
}

export function recordPartyA2mConsent(
  token: string,
  caseId: string,
  partyId: string,
  method: 'verbal' | 'in_person' | 'email' | 'imported' = 'verbal',
): Promise<{ ok: boolean; party_id: string; consented_at: string }> {
  return request(`/cases/${encodeURIComponent(caseId)}/parties/${encodeURIComponent(partyId)}/a2m-consent`, token, {
    method: 'POST',
    body: JSON.stringify({ method }),
  });
}

export type ClosedStatusKey =
  | 'blocked_no_contact'
  | 'blocked_refusal_to_pay'
  | 'blocked_other'
  | 'mediation_full_resolution'
  | 'mediation_partial_resolution'
  | 'mediation_no_resolution'
  | 'party_no_show'
  | 'rescheduled_other_mediator';

/** Court-vocabulary closed statuses (mirror of the engine map). */
export const CLOSED_STATUS_OPTIONS: Array<{ key: ClosedStatusKey; label: string }> = [
  { key: 'blocked_no_contact', label: 'Blocked - No Contact with a Party' },
  { key: 'blocked_refusal_to_pay', label: 'Blocked - Refusal to Pay' },
  { key: 'blocked_other', label: 'Blocked - Other' },
  { key: 'mediation_full_resolution', label: 'Mediation Complete - Full Resolution' },
  { key: 'mediation_partial_resolution', label: 'Mediation Complete - Partial Resolution' },
  { key: 'mediation_no_resolution', label: 'Mediation Complete - No Resolution' },
  { key: 'party_no_show', label: 'Mediation Scheduled - Party No-Show' },
  { key: 'rescheduled_other_mediator', label: 'Mediation being scheduled with another Mediator' },
];

export function logPartyCall(
  token: string,
  caseId: string,
  partyId: string,
  outcome: 'no_answer' | 'voicemail' | 'spoke' | 'bad_number',
  note?: string,
): Promise<{ ok: boolean }> {
  return request(`/cases/${encodeURIComponent(caseId)}/parties/${encodeURIComponent(partyId)}/log-call`, token, {
    method: 'POST',
    body: JSON.stringify({ outcome, ...(note ? { note } : {}) }),
  });
}

export function setFileCourtVisible(
  token: string,
  fileId: string,
  visible: boolean,
): Promise<{ ok: boolean; court_visible: boolean }> {
  return request(`/files/${encodeURIComponent(fileId)}/court-visible`, token, {
    method: 'POST',
    body: JSON.stringify({ visible }),
  });
}

export function fileCaseFileAsStipulation(token: string, fileId: string): Promise<{ ok: boolean; agreement_id: string }> {
  return request(`/files/${encodeURIComponent(fileId)}/file-as-stipulation`, token, { method: 'POST', body: '{}' });
}

export function setCaseNumber(
  token: string,
  caseId: string,
  caseNumber: string,
): Promise<{ ok: boolean; case_number: string; division: string | null }> {
  return request(`/cases/${encodeURIComponent(caseId)}/case-number`, token, {
    method: 'POST',
    body: JSON.stringify({ case_number: caseNumber }),
  });
}

export function closeCase(
  token: string,
  caseId: string,
  reason: string,
  closedStatus: ClosedStatusKey,
): Promise<{ id: string; status: string; closed_at: string }> {
  return request(`/cases/${encodeURIComponent(caseId)}/close`, token, {
    method: 'POST',
    body: JSON.stringify({ reason, closed_status: closedStatus }),
  });
}

/** Send a manually added party their Agreement to Mediate link (email +
    SMS); accepting on the tokened page records the same consent event as
    the intake checkbox. */
export function sendPartyA2m(
  token: string,
  caseId: string,
  partyId: string,
): Promise<{ ok: boolean; queued: number; skipped: string[] }> {
  return request(`/cases/${encodeURIComponent(caseId)}/parties/${encodeURIComponent(partyId)}/send-a2m`, token, {
    method: 'POST',
    body: '{}',
  });
}

/** Send a party the confirmed session details (time, Zoom link, phone
    dial-in, ICS) — for anyone added after the case was booked (an
    attorney, a co-party) who missed the original confirmation. */
export function sendPartySchedulingDetails(
  token: string,
  caseId: string,
  partyId: string,
): Promise<{ ok: boolean; session_time?: string; channels?: string[] }> {
  return request(`/cases/${encodeURIComponent(caseId)}/parties/${encodeURIComponent(partyId)}/send-scheduling`, token, {
    method: 'POST',
    body: '{}',
  });
}

/** Campus one-button escalation (Rob's CU flow): after the 15-min consult,
    the mediator invites the landlord (A2M), invoices both parties $70
    platform-direct, and queues the 2-hour joint mediation — which stays held
    until BOTH parties pay. Returns what it did so the UI can confirm. */
export function escalateToMediation(
  token: string,
  caseId: string,
): Promise<{ ok: boolean; landlord?: string; invoiced?: number }> {
  return request(`/cases/${encodeURIComponent(caseId)}/escalate-to-mediation`, token, {
    method: 'POST',
    body: '{}',
  });
}

/** Issue the bridge dial string to call this party FROM the source number.
    The mediator's cell must be verified (caller_registered) for the bridge
    to accept the call. */
export function callParty(
  token: string,
  caseId: string,
  partyId: string,
): Promise<{ ok: boolean; dial_string: string; bridge_number: string; party_number: string; source_number: string | null; caller_registered: boolean }> {
  return request(`/cases/${encodeURIComponent(caseId)}/parties/${encodeURIComponent(partyId)}/call`, token, {
    method: 'POST',
    body: '{}',
  });
}

/** Start one-time cell verification — texts a 6-digit code to the number. */
export function startPhoneVerification(token: string, phone: string): Promise<{ ok: boolean }> {
  return request('/auth/me/phone', token, { method: 'POST', body: JSON.stringify({ phone }) });
}

/** Confirm the texted code; on success the cell becomes the mediator's
    verified caller-ID for outbound calling. */
export function verifyPhone(token: string, phone: string, code: string): Promise<{ ok: boolean; phone: string }> {
  return request('/auth/me/phone/verify', token, { method: 'POST', body: JSON.stringify({ phone, code }) });
}

export function reopenCase(
  token: string,
  caseId: string,
  reason: string,
): Promise<{ ok: boolean; status: string }> {
  return request(`/cases/${encodeURIComponent(caseId)}/reopen`, token, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export function bookSessionManually(
  token: string,
  caseId: string,
  slotIso: string,
  overrideConflicts = false,
  opts: { category?: 'consultation' | 'mediation'; duration_minutes?: number } = {},
): Promise<{ task_id: string; slot: string }> {
  return request(`/cases/${encodeURIComponent(caseId)}/scheduling-tasks/book`, token, {
    method: 'POST',
    body: JSON.stringify({
      slot: slotIso,
      ...(overrideConflicts ? { override_conflicts: true } : {}),
      ...(opts.category ? { category: opts.category } : {}),
      ...(opts.duration_minutes ? { duration_minutes: opts.duration_minutes } : {}),
    }),
  });
}

export function rescheduleTask(token: string, taskId: string): Promise<{ ok: boolean }> {
  return request(`/scheduling-tasks/${encodeURIComponent(taskId)}/reschedule`, token, {
    method: 'POST',
    body: '{}',
  });
}

/** Cancel a mediation before it starts (a party backed out): tears down the
    booked session, stops reminders, and — unless notify is false — sends each
    party a "your mediation has been cancelled" notice. Does NOT close the case. */
export function cancelMediation(
  token: string,
  taskId: string,
  opts: { reason?: string; notify?: boolean } = {},
): Promise<{ ok: boolean; status: string; notified?: number }> {
  return request(`/scheduling-tasks/${encodeURIComponent(taskId)}/cancel`, token, {
    method: 'POST',
    body: JSON.stringify({ reason: opts.reason, notify: opts.notify }),
  });
}

export function getAvailability(token: string): Promise<Availability> {
  return request<Availability>('/mediators/me/availability', token, { method: 'GET' });
}

export function putAvailability(
  token: string,
  availability: Availability,
): Promise<{ ok: boolean }> {
  return request('/mediators/me/availability', token, {
    method: 'PUT',
    body: JSON.stringify(availability),
  });
}

// ============================================================
// Documents (Stip / ROM / resolution agreement)
// ============================================================

export function createDocument(
  token: string,
  caseId: string,
  docType: DocType,
): Promise<CreateDocumentResponse> {
  return request<CreateDocumentResponse>(
    `/cases/${encodeURIComponent(caseId)}/documents`,
    token,
    { method: 'POST', body: JSON.stringify({ doc_type: docType }) },
  );
}

export function listCaseDocuments(
  token: string,
  caseId: string,
): Promise<{ documents: DocumentRecord[] }> {
  return request<{ documents: DocumentRecord[] }>(
    `/cases/${encodeURIComponent(caseId)}/documents`,
    token,
    { method: 'GET' },
  );
}

export function getSigningStatus(token: string, agreementId: string): Promise<SigningStatus> {
  return request<SigningStatus>(
    `/documents/${encodeURIComponent(agreementId)}/signing-status`,
    token,
    { method: 'GET' },
  );
}

// ============================================================
// Sessions — join-url lookup for the capability-aware Join button.
// The Engine may not expose this to JWT principals yet; callers must
// treat failures as "no join link available".
// ============================================================

export function getEngineSession(
  token: string,
  sessionId: string,
): Promise<{ id: string; status: string; scheduled_start?: string | null; metadata: Record<string, unknown> }> {
  return request(`/mediator/sessions/${encodeURIComponent(sessionId)}`, token, {
    method: 'GET',
  });
}

// ── Case file attachments (photos, leases, signed paper agreements) ──

export interface CaseFile {
  id: string;
  file_name: string;
  label: string | null;
  content_type: string;
  size_bytes: number;
  uploaded_by: string | null;
  created_at: string;
  /** 'party_message' = a texted/emailed attachment (streams from the
      evidence endpoint, not /files). Absent = mediator upload. */
  source?: 'party_message';
  /** Mediator opted to surface this file in the clerk/judge view. */
  court_visible?: boolean;
}

export function listCaseFiles(token: string, caseId: string): Promise<{ files: CaseFile[] }> {
  return request<{ files: CaseFile[] }>(`/cases/${encodeURIComponent(caseId)}/files`, token);
}

/** Multipart upload — bypasses request() so the browser sets the boundary. */
export async function uploadCaseFile(
  token: string,
  caseId: string,
  file: File,
  label?: string,
  courtVisible?: boolean,
): Promise<CaseFile> {
  const form = new FormData();
  form.append('file', file);
  if (label) form.append('label', label);
  if (courtVisible) form.append('court_visible', 'true');
  const res = await fetch(`${env.resolutionEngineBaseUrl}/cases/${encodeURIComponent(caseId)}/files`, {
    method: 'POST',
    headers: { authorization: `Bearer ${token}` },
    body: form,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as ApiError | null;
    throw new EngineError(res.status, body?.error?.code ?? 'UPLOAD_FAILED', body?.error?.message ?? 'Upload failed');
  }
  return res.json() as Promise<CaseFile>;
}

/** Fetch the file bytes (auth header required — the bucket is private). */
export async function fetchCaseFileBlob(token: string, fileId: string): Promise<Blob> {
  const res = await fetch(`${env.resolutionEngineBaseUrl}/files/${encodeURIComponent(fileId)}`, {
    headers: { authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new EngineError(res.status, 'FILE_FETCH', 'Could not load the file');
  return res.blob();
}

/** The rendered document HTML (mediator view; opened in a new tab). */
export async function getDocumentHtml(token: string, agreementId: string): Promise<string> {
  const res = await fetch(
    `${env.resolutionEngineBaseUrl}/documents/${encodeURIComponent(agreementId)}/html`,
    { headers: { authorization: `Bearer ${token}` } },
  );
  if (!res.ok) throw new EngineError(res.status, 'DOCUMENT_HTML', 'Could not load the document');
  return res.text();
}

/** Stream the stored PDF through the authed endpoint (private bucket —
    raw storage links never serve). Returns a blob URL to open. */
/** Externally created Stipulation/ROM PDF becomes THE document of that
    type — lands in the Stip/ROM slots, clerk-visible only after
    shareDocumentWithCourt (Danny, 2026-09-14). */
export async function uploadExternalDocument(
  token: string,
  caseId: string,
  docType: 'stipulation' | 'rom',
  file: File,
): Promise<{ agreement: DocumentRecord }> {
  const form = new FormData();
  form.append('file', file);
  form.append('doc_type', docType);
  const res = await fetch(`${env.resolutionEngineBaseUrl}/cases/${encodeURIComponent(caseId)}/documents/upload`, {
    method: 'POST',
    headers: { authorization: `Bearer ${token}` },
    body: form,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as ApiError | null;
    throw new EngineError(res.status, body?.error?.code ?? 'UPLOAD_FAILED', body?.error?.message ?? 'Upload failed');
  }
  return (await res.json()) as { agreement: DocumentRecord };
}

export function unshareDocumentWithCourt(
  token: string,
  agreementId: string,
): Promise<{ ok: boolean }> {
  return request(`/documents/${encodeURIComponent(agreementId)}/unshare-court`, token, {
    method: 'POST',
    body: '{}',
  });
}

export function shareDocumentWithCourt(
  token: string,
  agreementId: string,
): Promise<{ ok: boolean; court_shared_at: string }> {
  return request(`/documents/${encodeURIComponent(agreementId)}/share-court`, token, {
    method: 'POST',
    body: '{}',
  });
}

export async function getDocumentPdfUrl(token: string, agreementId: string): Promise<string> {
  const res = await fetch(
    `${env.resolutionEngineBaseUrl}/documents/${encodeURIComponent(agreementId)}/pdf`,
    { headers: { authorization: `Bearer ${token}` } },
  );
  if (!res.ok) throw new EngineError(res.status, 'DOCUMENT_PDF', 'No PDF is available yet');
  return URL.createObjectURL(await res.blob());
}

/** Email/text every pending signer their private signing link. */
export function sendSignRequests(
  token: string,
  agreementId: string,
): Promise<Record<string, unknown>> {
  return request<Record<string, unknown>>(
    `/documents/${encodeURIComponent(agreementId)}/send-sign-requests`,
    token,
    // ai_reviewed: the UI gates the send behind the AI-review checkbox.
    { method: 'POST', body: JSON.stringify({ ai_reviewed: true }) },
  );
}

/** One-click tenant portal (magic-link) send — the NOMAD-native path. */
export function sendMagicLink(
  token: string,
  caseId: string,
): Promise<{ ok: boolean; channel: string; to: string }> {
  return request<{ ok: boolean; channel: string; to: string }>(
    `/cases/${encodeURIComponent(caseId)}/send-magic-link`,
    token,
    { method: 'POST', body: JSON.stringify({}) },
  );
}

/** Case notes — append-only internal mediator work product. */
export interface CaseNote {
  id: string;
  body: string;
  created_at: string;
  author_id: string;
  author_name: string;
}

export function listCaseNotes(token: string, caseId: string): Promise<{ notes: CaseNote[] }> {
  return request<{ notes: CaseNote[] }>(`/cases/${encodeURIComponent(caseId)}/notes`, token);
}

export function addCaseNote(token: string, caseId: string, body: string): Promise<CaseNote> {
  return request<CaseNote>(`/cases/${encodeURIComponent(caseId)}/notes`, token, {
    method: 'POST',
    body: JSON.stringify({ body }),
  });
}

/** Upcoming session on the mediator's agenda (Mission Control). */
export interface AgendaSession {
  id: string;
  case_id: string;
  status: string;
  session_type: 'joint' | 'single_party';
  scheduled_start: string;
  scheduled_duration_minutes: number | null;
  join_url: string | null;
  /** Host link (mediator-only agenda) — starting via this makes the
      assigned mediator the host, which is what fires RTMS + the sidebar. */
  start_url: string | null;
  /** Dial-in details to read to a party who can't launch Zoom. */
  phone_join: { number: string; passcode: string | null; meeting_id: string } | null;
  case_title_hint: string | null;
  claim_type: string | null;
  parties: Array<{ name: string; role_archetype: string }>;
  mediator_name: string | null;
}

/** Create a Zoom session starting RIGHT NOW, linked to the case (the
 *  in-meeting sidebar matches on the meeting id). Links are returned,
 *  never auto-sent — the mediator shares them deliberately. */
export function startInstantSession(
  token: string,
  caseId: string,
  overrideConflicts = false,
): Promise<{ session_id: string; zoom_meeting_id: string; join_url: string; start_url: string | null; phone_join: { number: string; passcode: string | null; meeting_id: string } | null }> {
  return request(`/cases/${encodeURIComponent(caseId)}/sessions/instant`, token, {
    method: 'POST',
    body: JSON.stringify(overrideConflicts ? { override_conflicts: true } : {}),
  });
}

export function getMyAgenda(token: string, days = 31): Promise<{ sessions: AgendaSession[] }> {
  return request<{ sessions: AgendaSession[] }>(`/mediators/me/agenda?days=${days}`, token);
}

// ── Admin area ──────────────────────────────────────────────────────

export interface AdminMediator {
  id: string;
  email: string;
  display_name: string;
  role: 'mediator' | 'admin' | 'clerk';
  judge_read_only: boolean;
  is_active: boolean;
  zoom_user_id: string | null;
  sources: Array<{ org_id: string; name: string; divisions?: string[] | null }>;
}

export interface AdminPartnerUser {
  id: string;
  org_id: string;
  org_name: string;
  email: string;
  display_name: string;
  is_active: boolean;
}

export interface AdminSource {
  id: string;
  name: string;
  api_key: string; // masked
  source_type: 'pm_firm' | 'court' | 'campus' | 'keel_direct';
  billing_config: Record<string, unknown>;
  intake_config: Record<string, unknown>;
  /** Dedicated E.164 SMS number for this source's outbound + inbound. */
  sms_from_number?: string | null;
}

export interface ResetIssued {
  reset_link: string;
  reset_emailed: boolean;
}

export const adminListMediators = (token: string) =>
  request<{ mediators: AdminMediator[] }>('/admin/mediators', token);
export const adminCreateMediator = (token: string, body: Record<string, unknown>) =>
  request<AdminMediator & ResetIssued>('/admin/mediators', token, { method: 'POST', body: JSON.stringify(body) });
export const adminPatchMediator = (token: string, id: string, body: Record<string, unknown>) =>
  request<{ ok: boolean }>(`/admin/mediators/${id}`, token, { method: 'PATCH', body: JSON.stringify(body) });
export const adminResetMediatorPassword = (token: string, id: string) =>
  request<ResetIssued>(`/admin/mediators/${id}/reset-password`, token, { method: 'POST', body: '{}' });

export const adminListPartnerUsers = (token: string) =>
  request<{ partner_users: AdminPartnerUser[] }>('/admin/partner-users', token);
export const adminCreatePartnerUser = (token: string, body: Record<string, unknown>) =>
  request<AdminPartnerUser & ResetIssued>('/admin/partner-users', token, { method: 'POST', body: JSON.stringify(body) });
export const adminPatchPartnerUser = (token: string, id: string, body: Record<string, unknown>) =>
  request<{ ok: boolean }>(`/admin/partner-users/${id}`, token, { method: 'PATCH', body: JSON.stringify(body) });
export const adminResetPartnerPassword = (token: string, id: string) =>
  request<ResetIssued>(`/admin/partner-users/${id}/reset-password`, token, { method: 'POST', body: '{}' });

export const adminListSources = (token: string) =>
  request<{ sources: AdminSource[] }>('/admin/sources', token);
export const adminCreateSource = (token: string, body: Record<string, unknown>) =>
  request<AdminSource & { api_key: string }>('/admin/sources', token, { method: 'POST', body: JSON.stringify(body) });
export const adminPatchSource = (token: string, id: string, body: Record<string, unknown>) =>
  request<{ ok: boolean }>(`/admin/sources/${id}`, token, { method: 'PATCH', body: JSON.stringify(body) });
export const adminRotateSourceKey = (token: string, id: string) =>
  request<{ org_id: string; api_key: string }>(`/admin/sources/${id}/rotate-key`, token, { method: 'POST', body: '{}' });

export const impersonate = (token: string, mediatorId: string) =>
  request<{ token: string; expires_in: number; principal: Principal; impersonator_id: string }>(
    '/auth/impersonate', token, { method: 'POST', body: JSON.stringify({ mediator_id: mediatorId }) });

export const changePassword = (token: string, currentPassword: string, newPassword: string) =>
  request<{ ok: boolean }>('/auth/change-password', token, {
    method: 'POST',
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });

export const forgotPassword = (email: string) =>
  request<{ ok: boolean }>('/auth/forgot-password', null, {
    method: 'POST',
    body: JSON.stringify({ email }),
  });

// ── Mediator Google Calendar sync (023) ──────────────────────────────

export interface CalendarConnection {
  provider: 'google' | 'microsoft';
  account_email: string | null;
  status: 'active' | 'revoked' | 'error';
  push_enabled: boolean;
  last_synced_at: string | null;
  last_sync_error: string | null;
}

export function getCalendarConnection(
  token: string,
): Promise<{ configured: boolean; connection: CalendarConnection | null }> {
  return request('/mediators/me/calendar', token, { method: 'GET' });
}

export function startCalendarConnect(token: string): Promise<{ auth_url: string }> {
  return request('/mediators/me/calendar/connect', token, { method: 'POST', body: '{}' });
}

export function disconnectCalendar(token: string): Promise<{ ok: boolean }> {
  return request('/mediators/me/calendar', token, { method: 'DELETE' });
}

// ── Cohort outreach launch ───────────────────────────────────────────

export interface OutreachEligible {
  case_id: string;
  tenant_name: string;
  channels: string[];
  balance: number;
  property: string | null;
}
export interface OutreachBlocked {
  case_id: string;
  tenant_name: string | null;
  reason: string;
}

export function getOutreachCohorts(token: string): Promise<{ cohorts: Array<{ cohort: string; count: number }> }> {
  return request('/comms/outreach/cohorts', token, { method: 'GET' });
}

export function launchOutreach(
  token: string,
  args: { cohort: string; dry_run: boolean },
): Promise<{ dry_run?: boolean; approved?: number; eligible: OutreachEligible[]; blocked: OutreachBlocked[] }> {
  return request('/comms/outreach/launch', token, { method: 'POST', body: JSON.stringify(args) });
}

// ============================================================
// ROM review — PATCH /documents/:id (participants/notes) + approve
// ============================================================

export interface RomEdits {
  participants: Array<{ name: string; role_label: string }>;
  notes: string | null;
  report_text?: string | null;
}

export function updateRomDocument(
  token: string,
  agreementId: string,
  rom: RomEdits,
): Promise<{ ok: boolean; pdf_url: string | null }> {
  return request(`/documents/${encodeURIComponent(agreementId)}`, token, {
    method: 'PATCH',
    body: JSON.stringify({ rom }),
  });
}

export interface RomEditorState {
  status: string;
  participants: Array<{ name: string; role_label: string }>;
  report_text: string;
  notes: string;
}

export function getRomEditorState(token: string, agreementId: string): Promise<RomEditorState> {
  return request(`/documents/${encodeURIComponent(agreementId)}/rom`, token, { method: 'GET' });
}

export function sendRomToParties(
  token: string,
  agreementId: string,
): Promise<{ ok: boolean; queued: number; skipped: string[]; status: string }> {
  return request(`/documents/${encodeURIComponent(agreementId)}/send-rom`, token, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function approveDocument(
  token: string,
  agreementId: string,
): Promise<{ ok: boolean; status: string }> {
  return request(`/documents/${encodeURIComponent(agreementId)}/approve`, token, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

// ============================================================
// Clerk outreach surface — attempt METADATA only (date/channel/party)
// ============================================================

export interface OutreachAttempt {
  at: string;
  channel: 'sms' | 'email' | 'call' | string;
  status: string;
}

export interface PartyOutreach {
  party_id: string;
  name: string;
  role: string;
  role_archetype: string;
  email: string | null;
  phone: string | null;
  attempts: OutreachAttempt[];
  counts: { email: number; sms: number; call: number };
}

export function getCaseOutreach(token: string, caseId: string): Promise<{ parties: PartyOutreach[] }> {
  return request(`/cases/${encodeURIComponent(caseId)}/outreach`, token, { method: 'GET' });
}

export function logCallAttempt(token: string, caseId: string, partyId: string): Promise<{ ok: boolean }> {
  return request(`/cases/${encodeURIComponent(caseId)}/call-log`, token, {
    method: 'POST',
    body: JSON.stringify({ party_id: partyId }),
  });
}

export function sendClerkMessage(token: string, caseId: string, body: string): Promise<{ ok: boolean }> {
  return request(`/cases/${encodeURIComponent(caseId)}/clerk-message`, token, {
    method: 'POST',
    body: JSON.stringify({ body }),
  });
}

export interface SourceInfo {
  id: string;
  name: string;
  source_type: string;
  logo_url: string | null;
}

export function getMySources(token: string): Promise<{ sources: SourceInfo[] }> {
  return request('/mediators/me/sources', token, { method: 'GET' });
}

// ============================================================
// Internal messaging — court staff <-> Levelheaded staff, per case
// ============================================================

export interface InternalMessage {
  id: string;
  body: string;
  created_at: string;
  author_id: string;
  author_name: string;
  author_side: 'court' | 'levelheaded';
}

export function getInternalMessages(
  token: string,
  caseId: string,
): Promise<{ messages: InternalMessage[]; unread_count: number }> {
  return request(`/cases/${encodeURIComponent(caseId)}/internal-messages`, token, { method: 'GET' });
}

/** Clerk list archive toggle — server-side, shared across the org's
    clerk/judge accounts (metadata.clerk_archived). */
export function setClerkArchived(
  token: string,
  caseId: string,
  archived: boolean,
): Promise<{ ok: boolean; archived: boolean }> {
  return request(`/cases/${encodeURIComponent(caseId)}/clerk-archive`, token, {
    method: 'POST',
    body: JSON.stringify({ archived }),
  });
}

export function sendInternalMessage(
  token: string,
  caseId: string,
  body: string,
): Promise<{ ok: boolean; message_id: string }> {
  return request(`/cases/${encodeURIComponent(caseId)}/internal-messages`, token, {
    method: 'POST',
    body: JSON.stringify({ body }),
  });
}

// ============================================================
// Manual overrides (spec 5.x)
// ============================================================

/** Full-page stipulation editor (Mo's Loom, 2026-09-15). */
export interface EditableStipulation {
  agreement_id: string;
  doc_type?: string;
  status: string;
  body_html: string;
  caption_overrides: Record<string, unknown> | null;
  fields?: { case_no: string; plaintiff: string; defendants: string[]; dated_on: string; division?: string; courtroom?: string };
  templates: Array<{ key: string; label: string; caption_overrides: Record<string, unknown> | null; body_html?: string | null }>;
  prefix_html: string | null;
  suffix_html: string | null;
  /** The signature block rendered from the LIVE signatures rows — appended
      after the body so previews reflect current signer names. */
  signature_html?: string | null;
  styles_css: string;
}

export function getEditableStipulation(token: string, agreementId: string): Promise<EditableStipulation> {
  return request(`/documents/${encodeURIComponent(agreementId)}/editable`, token, { method: 'GET' });
}

export function saveStipulationBody(
  token: string,
  agreementId: string,
  edits: { body_html?: string; caption_overrides?: Record<string, unknown> | null },
): Promise<{ ok: boolean }> {
  return request(`/documents/${encodeURIComponent(agreementId)}`, token, {
    method: 'PATCH',
    body: JSON.stringify({ stipulation: edits }),
  });
}

/** Live delinquency wave report (Danny, 2026-09-15). */
export interface DelinquencyReport {
  generated_at: string;
  orgs: Array<{ id: string; name: string }>;
  org_id: string;
  totals: {
    cases: number; contacted: number; intakes: number; a2m: number; accepted: number;
    proposals: number; inbound: number; resolved: number; committed_usd: number; escalating: number;
    resolved_external: number; sessions_held: number; sessions_upcoming: number; off_file_review: number;
  };
  delivery: { delivered: number; failed: number; in_flight: number; contacted_cases: number };
  cohorts: Array<{ cohort: string; cases: number; contacted: number; intakes: number; a2m: number; accepted: number; proposals: number; inbound: number; resolved: number; committed_usd: number; escalating: number }>;
  resolved_cases: Array<{ at: string; kind: string; amount: number }>;
  upcoming_sessions: string[];
}

export function getDelinquencyReport(token: string, orgId?: string): Promise<DelinquencyReport | { orgs: []; report: null }> {
  const qs = orgId ? `?org_id=${encodeURIComponent(orgId)}` : '';
  return request(`/metrics/delinquency-report${qs}`, token, { method: 'GET' });
}

export function dictateAgreementText(
  token: string,
  caseId: string,
  text: string,
): Promise<{ agreement_id: string; doc_type: string; status: string; terms: Array<{ section: string; terms: string[] }> }> {
  return request(`/mediator/cases/${encodeURIComponent(caseId)}/dictate-agreement`, token, {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
}

/** Signer management on a draft/sent document (Mo, 2026-09-16). */
export interface SignerRecord {
  id: string;
  signer_name: string;
  role_label: string | null;
  status: string;
  signer_email?: string | null;
  signer_phone?: string | null;
  sign_url: string | null;
}

export function addDocumentSigner(
  token: string,
  agreementId: string,
  signer: { name: string; role_label?: string; email?: string; phone?: string },
): Promise<{ signer: SignerRecord }> {
  return request(`/documents/${encodeURIComponent(agreementId)}/signers`, token, {
    method: 'POST',
    body: JSON.stringify(signer),
  });
}

export function updateDocumentSigner(
  token: string,
  agreementId: string,
  sigId: string,
  updates: { name?: string; role_label?: string; email?: string; phone?: string },
): Promise<{ signer: SignerRecord }> {
  return request(`/documents/${encodeURIComponent(agreementId)}/signers/${encodeURIComponent(sigId)}`, token, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

export function removeDocumentSigner(
  token: string,
  agreementId: string,
  sigId: string,
): Promise<{ ok: boolean }> {
  return request(`/documents/${encodeURIComponent(agreementId)}/signers/${encodeURIComponent(sigId)}`, token, {
    method: 'DELETE',
  });
}

export function updateStipulationTerms(
  token: string,
  agreementId: string,
  terms: string[],
): Promise<{ ok: boolean; pdf_url: string | null }> {
  return request(`/documents/${encodeURIComponent(agreementId)}`, token, {
    method: 'PATCH',
    body: JSON.stringify({ stipulation: { terms } }),
  });
}

export function voidDocument(token: string, agreementId: string, reason?: string): Promise<{ ok: boolean; status: string }> {
  return request(`/documents/${encodeURIComponent(agreementId)}/void`, token, {
    method: 'POST',
    body: JSON.stringify(reason ? { reason } : {}),
  });
}

export function createInvoice(
  token: string,
  caseId: string,
  input: { party_id: string; amount_cents?: number; memo?: string },
): Promise<PartyInvoice> {
  return request(`/cases/${encodeURIComponent(caseId)}/invoices`, token, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function voidInvoice(token: string, invoiceId: string): Promise<{ invoice: { id: string; status: string } }> {
  return request(`/invoices/${encodeURIComponent(invoiceId)}/void`, token, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

/** Refund a PAID invoice (Danny, 2026-10-20). Admin-only in-route; the button
    is only shown to admins, and a non-admin hit returns a clear 403. */
export function refundInvoice(
  token: string,
  invoiceId: string,
): Promise<{ invoice: { id: string; status: string; paid_at: null }; stripe: { id: string } }> {
  return request(`/invoices/${encodeURIComponent(invoiceId)}/refund`, token, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function cancelSignRequests(
  token: string,
  agreementId: string,
  opts: { reopenSigned?: boolean } = {},
): Promise<{ ok: boolean; status: string; signatures_reset: number }> {
  return request(`/documents/${encodeURIComponent(agreementId)}/cancel-sign-requests`, token, {
    method: 'POST',
    body: JSON.stringify(opts.reopenSigned ? { reopen_signed: true } : {}),
  });
}

/** Manual-stip dictation (Danny ticket, 2026-09-15): the mediator recites
    the resolution aloud; the engine transcribes it and the same drafting
    model produces the same reviewable document the Zoom flow does. */
export async function dictateAgreement(
  token: string,
  caseId: string,
  audio: Blob,
): Promise<{ agreement_id: string; doc_type: string; status: string; dictation_transcript: string; terms: Array<{ section: string; terms: string[] }> }> {
  const form = new FormData();
  form.append('audio', new File([audio], 'dictation.webm', { type: audio.type || 'audio/webm' }));
  const res = await fetch(`${env.resolutionEngineBaseUrl}/mediator/cases/${encodeURIComponent(caseId)}/dictate-agreement`, {
    method: 'POST',
    headers: { authorization: `Bearer ${token}` },
    body: form,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as ApiError | null;
    throw new EngineError(res.status, body?.error?.code ?? 'DICTATE_FAILED', body?.error?.message ?? 'Dictation drafting failed');
  }
  return (await res.json()) as { agreement_id: string; doc_type: string; status: string; dictation_transcript: string; terms: Array<{ section: string; terms: string[] }> };
}

// ============================================================
// Practice self-service (white-label tenants — Mediate Colorado)
// ============================================================

export interface PracticeSource {
  id: string; name: string; source_type: string; sms_from_number: string | null;
  slug: string | null; logo_url: string | null; support_email: string | null; support_phone: string | null;
  created_at: string;
}
export interface PracticeMediator {
  id: string; email: string; display_name: string; role: string; is_active: boolean; created_at?: string;
}
export interface PracticePayload {
  practice: { id: string; name: string; brand_config: Record<string, unknown>; status: string };
  sources: PracticeSource[];
  mediators: PracticeMediator[];
}

export function getPractice(token: string): Promise<PracticePayload> {
  return request<PracticePayload>(`/practice`, token);
}
export function patchPracticeBrand(token: string, body: Record<string, string>): Promise<{ practice: PracticePayload['practice'] }> {
  return request(`/practice`, token, { method: 'PATCH', body: JSON.stringify(body) });
}
export function createPracticeSource(token: string, body: { name: string; source_type?: string; slug: string; logo_url?: string; support_email?: string; support_phone?: string }): Promise<{ source: PracticeSource; intake_path: string }> {
  return request(`/practice/sources`, token, { method: 'POST', body: JSON.stringify(body) });
}
export function patchPracticeSource(token: string, id: string, body: Record<string, string>): Promise<{ source: PracticeSource }> {
  return request(`/practice/sources/${encodeURIComponent(id)}`, token, { method: 'PATCH', body: JSON.stringify(body) });
}
export function createPracticeMediator(token: string, body: { email: string; display_name: string; role?: 'mediator' | 'admin' | 'admin_mediator' }): Promise<{ mediator: PracticeMediator; temp_password: string }> {
  return request(`/practice/mediators`, token, { method: 'POST', body: JSON.stringify(body) });
}
export function patchPracticeMediator(token: string, id: string, body: { display_name?: string; role?: string; is_active?: boolean }): Promise<{ mediator: PracticeMediator }> {
  return request(`/practice/mediators/${encodeURIComponent(id)}`, token, { method: 'PATCH', body: JSON.stringify(body) });
}

export const adminAttachPracticeSource = (token: string, practiceId: string, orgId: string) =>
  request<{ ok: boolean }>(`/admin/practices/${encodeURIComponent(practiceId)}/attach-source`, token, {
    method: 'POST', body: JSON.stringify({ org_id: orgId }),
  });

// ── Signed-agreements report (artifact-style monthly agreements view) ──
export interface SignedAgreementRow {
  agreement_id: string; case_id: string; doc_type: string; signed_at: string;
  tenant: string; mediator: string | null;
  plan_type: string | null; terms_label: string | null; plan_total: number | null; payoff_by: string | null;
}
export interface SignedAgreementsReport {
  orgs: Array<{ id: string; name: string }>;
  report: {
    org_id: string; window: { from: string; to: string };
    kpis: { signed: number; settled_value_usd: number; settled_parameterized: number; resolved_cases: number };
    plan_types: Record<string, number>;
    rows: SignedAgreementRow[];
  } | null;
}
export function getSignedAgreementsReport(token: string, opts: { org_id?: string; from?: string; to?: string } = {}): Promise<SignedAgreementsReport> {
  const qs = new URLSearchParams(Object.entries(opts).filter(([, v]) => !!v) as [string, string][]).toString();
  return request(`/metrics/signed-agreements${qs ? `?${qs}` : ''}`, token, { method: 'GET' });
}
